import { t } from '../../copy'
import { T } from '../../copy/text'
import { canAddMore, humanBytes, type DraftAttachment } from './attachments'

/**
 * 输入框上方那一排附件（设计稿输入框工具条「附件 · 加附件」）。
 *
 * # 四态都在屏幕上，不是只有成功那一态
 *
 * uploading 有进度条、failed 有原因与「再试一次」、done 有「不要了」。
 * 少画任何一态的后果都是同一个形状：**用户看着一个不动的东西，
 * 不知道它是在传、传坏了、还是已经好了。**
 *
 * # 为什么「不要了」而不是「删除」
 *
 * 已经发出去的附件是那条消息的一部分，删不掉（服务端 404）。
 * 这一排里只有还没发出去的，「不要了」说的是「这次别带它」——
 * 而「删除」会让人以为发出去的那些也能撤。
 */
export function AttachmentTray({
  items,
  onRetry,
  onRemove,
  notice,
}: {
  readonly items: readonly DraftAttachment[]
  /** 重试一个失败的。不给就不出重试按钮（点不动的按钮是错的形状）。 */
  readonly onRetry?: (localId: string) => void
  readonly onRemove?: (localId: string) => void
  /**
   * 刚发生的一句拦截（太大 / 类型不收 / 挂太多）。
   *
   * **它不是某一个附件的状态**——被拦下的那个文件根本没进这个列表。
   * 混进列表里会多出一行「不存在的附件」。
   */
  readonly notice?: 'too_large' | 'bad_type' | 'too_many' | null
}) {
  if (items.length === 0 && !notice) return null
  return (
    <div data-testid="lulu-attach-tray" className="mb-2 grid gap-1">
      {items.map((a) => (
        <div
          key={a.localId}
          data-testid="lulu-attach-item"
          data-state={a.state}
          className="grid min-w-0 gap-1 rounded-npc-chip border border-npc-lulu-line-2 bg-npc-lulu-bg-1 px-2 py-1"
        >
          {/* 第一行只放长度可控的东西：名字（会截断）、大小、按钮。
              失败那句人话不在这里——它长度由服务端说了算，见下面那一行。 */}
          {/* # 这两处 `min-w-0` 都不是可选的，而且**缺一处就不够**
              （2026-09-02 在真浏览器、真 Tailwind、390 档上逐档量出来的）

              grid/flex 子项的 `min-width` 默认是 `auto`，解析成它的
              min-content；而这一行里那个 `white-space: nowrap` 的文件名
              让 min-content 等于整串名字的宽度。于是轨道被撑到比容器还宽。

              358px 容器里放一个长文件名，实测：

                  原样                       tray scrollWidth 566  ← 溢出 208
                  只给外层 item 加 min-w-0    tray scrollWidth 557  ← 仍溢出 199
                  外层 item + 这一行都加       tray scrollWidth 358  ← 干净

              **中间那一行是这条注释存在的理由**：只加一处会让人以为修好了，
              而屏幕上照样横着捅出去。外层那个 `grid` 让这一行也成了 grid 子项，
              所以它自己的 `min-width:auto` 得单独解掉。

              名字那一格上的 `min-w-0` 拦不住任何一层——它管的是名字在
              这一行里的收缩，不是这一行在外层里的收缩。 */}
          <div className="flex min-w-0 items-center gap-2">
            {/* min-w-0 + truncate：长文件名截断而非撑破（验收 #43 那个坑）。 */}
            <span
              data-testid="lulu-attach-name"
              className="min-w-0 flex-1 truncate text-npc-caption text-npc-lulu-t2"
            >
              {a.name}
            </span>
            <span className="shrink-0 text-npc-meta text-npc-lulu-t4">{humanBytes(a.bytes)}</span>

            {a.state === 'uploading' ? (
              <span
                data-testid="lulu-attach-progress"
                data-percent={a.percent}
                className="shrink-0 text-npc-meta text-npc-lulu-t3"
              >
                <T id="lulu_workbench.attach.uploading" />
                {' \u00b7 '}
                {a.percent}%
              </span>
            ) : null}

            {a.state === 'failed' && onRetry !== undefined ? (
              <button
                type="button"
                data-testid="lulu-attach-retry"
                onClick={() => onRetry(a.localId)}
                className="shrink-0 rounded-npc-tag border border-npc-lulu-line-2 px-2 py-0.5 text-npc-meta text-npc-lulu-t2"
              >
                <T id="lulu_workbench.attach.retry" />
              </button>
            ) : null}

            {onRemove === undefined || a.state === 'uploading' ? null : (
              <button
                type="button"
                data-testid="lulu-attach-remove"
                onClick={() => onRemove(a.localId)}
                className="shrink-0 rounded-npc-tag border border-npc-lulu-line-2 px-2 py-0.5 text-npc-meta text-npc-lulu-t3"
              >
                <T id="lulu_workbench.attach.remove" />
              </button>
            )}
          </div>

          {/* 失败原因**自己一行**。
              它坐在上面那个 flex 里的时候，390 档量到 366 的格子里横着捅出
              664px——而唯一能缩的名字被挤成 0px 宽，屏幕上是「文件名没了，
              一条报错横出去」。这句话有多长是服务端说了算的，所以它只能
              有自己的一行，并且要允许断行。 */}
          {a.state === 'failed' ? (
            <span
              data-testid="lulu-attach-failed"
              className="break-words text-npc-meta text-npc-lulu-warn"
            >
              {/* 服务端那句人话优先。**读不出来才用兜底那句**——
                  编一个原因会把用户送去改一个没问题的地方。 */}
              {a.error && a.error !== '' ? a.error : t('lulu_workbench.attach.failed')}
            </span>
          ) : null}
        </div>
      ))}

      {notice ? (
        <p data-testid="lulu-attach-notice" data-kind={notice} className="text-npc-meta text-npc-lulu-warn">
          <T
            id={
              notice === 'too_large'
                ? 'lulu_workbench.attach.too_large'
                : notice === 'bad_type'
                  ? 'lulu_workbench.attach.bad_type'
                  : 'lulu_workbench.attach.too_many'
            }
          />
        </p>
      ) : null}
    </div>
  )
}

/** 那颗「加附件」按钮。挂满了就按不动，并说清为什么。 */
export function AttachButton({
  items,
  onPick,
}: {
  readonly items: readonly DraftAttachment[]
  /** 不给就整颗不画——点不动的按钮是错的形状。 */
  readonly onPick?: () => void
}) {
  if (onPick === undefined) return null
  const more = canAddMore(items)
  return (
    <button
      type="button"
      data-testid="lulu-attach-button"
      disabled={!more}
      title={t('lulu_workbench.attach.action')}
      aria-label={t('lulu_workbench.attach.action')}
      onClick={() => {
        if (!more) return
        onPick()
      }}
      className="inline-flex h-7 shrink-0 items-center rounded-npc-tag border border-npc-lulu-line-2 px-2 text-npc-meta text-npc-lulu-t2 disabled:opacity-40"
    >
      <T id="lulu_workbench.attach.action" />
    </button>
  )
}
